import {DatePipe} from '@angular/common';
import {Component} from '@angular/core';
import {
    ColumnFilter,
    DataTableColumnData,
    DataTableConfig,
    DataTablePaginatedDataSource,
    getErrorMessage,
    mapPaginatedOrNonPaginatedResponse,
    TableAction
} from '@cas-hub/acabim-common-client';
import {MessageService} from 'primeng/api';
import {map} from 'rxjs/operators';
import {OrganisationSeat} from '../../../_models/organisation/organisation-seats.model';
import {OrganisationSeatsService} from '../../../_services/organisation/organisation-seats.service';

@Component({
    selector: 'acabim-admin-organisation-seats',
    templateUrl: './organisation-seats.component.html',
    styleUrls: ['./organisation-seats.component.scss']
})
export class OrganisationSeatsComponent extends DataTablePaginatedDataSource<OrganisationSeat> {

    public readonly actions: TableAction[] = [
        {id: 0, name: 'Remove Seat'}
    ];

    public readonly columnData: DataTableColumnData[] = [
        {displayName: 'Order Number', objectPropertyName: 'seatId'},
        {displayName: 'Number Of Seats', objectPropertyName: 'numberOfSeats', editable: true},
        {displayName: 'Usage Count', objectPropertyName: 'usageCount'},
        {displayName: 'Remaining Seats', objectPropertyName: 'remainingSeats'},
        {displayName: 'Remaining Days', objectPropertyName: 'remainingDays'},
        {displayName: 'Ordered by', objectPropertyName: 'orderedUser.name'},
        {displayName: 'Ordered Date', objectPropertyName: 'orderedDate'},
        {displayName: 'Purchase Date', objectPropertyName: 'purchaseDate'},
        {displayName: 'Restricted Solution (if any)', objectPropertyName: 'attachedSolution'},
        {displayName: 'Restricted Role (if any)', objectPropertyName: 'attachedRole'}
    ];

    public readonly columnFilters: ColumnFilter[] = [];

    public readonly config: DataTableConfig = {
        globalFilterFields: [],
        hasActions: true,
        hasCheckbox: true,
        hasDetailsButton: false,
        hasFilters: false,
        isExportEnabled: false,
        isLazyLoadingEnabled: true,
        isPaginationEnabled: true,
        isSearchEnabled: false,
        searchPlaceHolder: '',
        tableHeading: '',
        emptyMessage: 'No Seat orders found'
    };

    private readonly seatNormaliser = (seat: OrganisationSeat): OrganisationSeat => {
        return {
            ...seat,
            orderedDate: this.datePipe.transform(new Date(seat.orderedDate), 'dd MMMM yyyy') as string,
            purchaseDate: seat.purchaseDate ? this.datePipe.transform(new Date(seat.purchaseDate), 'dd MMMM yyyy') as string : undefined
        };
    }

    public constructor(private readonly seatsService: OrganisationSeatsService,
                       private readonly messageService: MessageService,
                       private readonly datePipe: DatePipe) {
        super((pagination, _) => seatsService.getSeatOrders(pagination)
            .pipe(map(x => mapPaginatedOrNonPaginatedResponse(x, this.seatNormaliser))));
    }

    public onAction(action: TableAction): void {
        if (this.selectedData) {
            const subscriptions: Promise<any>[] = [];
            for (const seat of this.selectedData) {
                if (seat.usageCount > 0) {
                    this.messageService.add({
                        severity: 'error',
                        key: 'subscription-seats',
                        summary: 'Unable to delete seat',
                        detail: `Unable to delete seat ${seat.seatId}. Seat has ${seat.usageCount} allocated users`
                    });
                } else {
                    subscriptions.push(this.seatsService.removePurchaseOrder(seat.seatId).toPromise());
                }
            }

            if (subscriptions.length > 0) {
                Promise.all(subscriptions).then(_ => this.onRequestToRefreshData()).catch(error => {
                    this.messageService.add(getErrorMessage(error, 'Unable to delete seat', 'subscription-seats'));
                });
            }
        }
    }

    public onDataExpanded(value: OrganisationSeat): void {
    }

    public onDataUpdated(content: OrganisationSeat): void {
        const originalSeat = this.rowData.find(s => s.seatId === content.seatId);
        if (originalSeat && originalSeat.numberOfSeats !== content.numberOfSeats) {
            this.seatsService.updatePurchaseOrder(content.seatId, content.numberOfSeats).subscribe(seat => {
                this.rowData.splice(this.rowData.findIndex(x => x.seatId === originalSeat.seatId), 1, this.seatNormaliser(seat));
            }, error => this.messageService.add(getErrorMessage(error, 'Unable to change seat', 'subscription-seats')));
        }
    }
}
